import type { AccommodationMetadata, BedType, UpdatePlaceData, WifiSpeed } from "~/lib/api";
import { pluralize } from "~/lib/format";
import { cn } from "~/lib/utils";
import { Button } from "~/components/ui/button";
import { Icon, type IconType } from "~/components/ui/icons/icon";
import { Tag } from "~/components/ui/tag";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/components/ui/select";
import { Switch } from "~/components/ui/switch";
import { InfoField } from "~/components/place/InfoField";
import { NumberEditor } from "~/components/place/NumberEditor";
import { PanelSection } from "~/components/ui/panel-section";
import { BedTypeEditor } from "./BedTypeEditor";
import {
  BED_TYPE_LABELS,
  WIFI_SPEED_HINTS,
  WIFI_SPEED_LABELS,
  WIFI_STABILITY_LABELS,
  formatWorkSummary,
} from "./accommodationLabels";
import { type Amenity, AMENITIES, AMENITY_LABELS } from "./amenities";

const BED_TYPES = Object.keys(BED_TYPE_LABELS) as BedType[];

const WIFI_FIELDS = [
  {
    field: "speed" as const,
    label: "Wifi speed",
    fieldIcon: "wifi-speed" as IconType,
    options: Object.entries(WIFI_SPEED_LABELS).map(([value, label]) => ({
      value,
      label,
      hint: WIFI_SPEED_HINTS[value as WifiSpeed],
      icon: `wifi-${value}` as IconType,
    })),
  },
  {
    field: "stability" as const,
    label: "Wifi stability",
    fieldIcon: "wifi-stability" as IconType,
    options: Object.entries(WIFI_STABILITY_LABELS).map(([value, label]) => ({
      value,
      label,
      hint: undefined as string | undefined,
      icon: undefined as IconType | undefined,
    })),
  },
];

interface AccommodationInfoSectionProps {
  metadata: AccommodationMetadata | null;
  canEdit?: boolean;
  onUpdate?: (data: UpdatePlaceData) => Promise<void>;
}

export function AccommodationInfoSection({ metadata, canEdit, onUpdate }: AccommodationInfoSectionProps) {
  const hasContent = metadata && hasValues(metadata);
  if (!canEdit && !hasContent) return null;

  const handleFieldUpdate = (field: "guests" | "bedrooms" | "bathrooms", value: number | null) => {
    onUpdate?.({ metadata: applyField(metadata, field, value) });
  };

  const handleBedUpdate = (bedType: BedType, count: number | null) => {
    onUpdate?.({ metadata: applyBed(metadata, bedType, count) });
  };

  const handlePolicyToggle = (field: "petsAllowed" | "childrenAllowed", checked: boolean) => {
    onUpdate?.({ metadata: applyPolicy(metadata, field, checked) });
  };

  const handleAmenityAdd = (amenity: Amenity) => {
    onUpdate?.({ metadata: { ...metadata, amenities: [...(metadata?.amenities ?? []), amenity] } });
  };

  const handleAmenityRemove = (amenity: Amenity) => {
    onUpdate?.({ metadata: applyAmenityRemoval(metadata, amenity) });
  };

  const selectedAmenities = AMENITIES.filter((a) => metadata?.amenities?.includes(a));
  const availableAmenities = AMENITIES.filter((a) => !selectedAmenities.includes(a));
  const bedEntries = getBedEntries(metadata?.beds);
  const unusedBedTypes = BED_TYPES.filter((t) => !bedEntries.some(([bt]) => bt === t));
  const sections = sectionVisibility(metadata, canEdit, selectedAmenities);

  return (
    <>
      {sections.property && (
        <PropertySection
          metadata={metadata}
          canEdit={canEdit}
          bedEntries={bedEntries}
          unusedBedTypes={unusedBedTypes}
          onFieldUpdate={handleFieldUpdate}
          onBedUpdate={handleBedUpdate}
        />
      )}
      {sections.guestPolicies && (
        <GuestPoliciesSection metadata={metadata} canEdit={canEdit} onToggle={handlePolicyToggle} />
      )}
      {sections.remoteWork && <RemoteWorkSection metadata={metadata} canEdit={canEdit} onUpdate={onUpdate} />}
      {sections.amenities && (
        <AmenitiesSection
          selectedAmenities={selectedAmenities}
          availableAmenities={availableAmenities}
          canEdit={canEdit}
          onAdd={handleAmenityAdd}
          onRemove={handleAmenityRemove}
        />
      )}
    </>
  );
}

interface PropertySectionProps {
  metadata: AccommodationMetadata | null;
  canEdit?: boolean;
  bedEntries: [BedType, number][];
  unusedBedTypes: BedType[];
  onFieldUpdate: (field: "guests" | "bedrooms" | "bathrooms", value: number | null) => void;
  onBedUpdate: (bedType: BedType, count: number | null) => void;
}

function PropertySection({
  metadata,
  canEdit,
  bedEntries,
  unusedBedTypes,
  onFieldUpdate,
  onBedUpdate,
}: PropertySectionProps) {
  return (
    <>
      <PanelSection.Separator />
      <PanelSection.Root>
        <PanelSection.Title>Property</PanelSection.Title>
        <PanelSection.Content className="space-y-3">
          {canEdit ? (
            <PropertyEditor
              metadata={metadata}
              bedEntries={bedEntries}
              unusedBedTypes={unusedBedTypes}
              onFieldUpdate={onFieldUpdate}
              onBedUpdate={onBedUpdate}
            />
          ) : (
            <AccommodationPropertyDetails metadata={metadata} />
          )}
        </PanelSection.Content>
      </PanelSection.Root>
    </>
  );
}

interface PropertyEditorProps {
  metadata: AccommodationMetadata | null;
  bedEntries: [BedType, number][];
  unusedBedTypes: BedType[];
  onFieldUpdate: (field: "guests" | "bedrooms" | "bathrooms", value: number | null) => void;
  onBedUpdate: (bedType: BedType, count: number | null) => void;
}

function PropertyEditor({ metadata, bedEntries, unusedBedTypes, onFieldUpdate, onBedUpdate }: PropertyEditorProps) {
  return (
    <>
      <InfoField label="Guests" icon="users">
        <NumberEditor
          value={metadata?.guests}
          onChange={(v) => onFieldUpdate("guests", v)}
          canEdit
          label="guests"
          display={(n) => pluralize(n, "guest")}
        />
      </InfoField>
      <InfoField label="Bedrooms" icon="bedroom">
        <NumberEditor
          value={metadata?.bedrooms}
          onChange={(v) => onFieldUpdate("bedrooms", v)}
          canEdit
          label="bedrooms"
        />
      </InfoField>
      <InfoField label="Bathrooms" icon="bathroom">
        <NumberEditor
          value={metadata?.bathrooms}
          onChange={(v) => onFieldUpdate("bathrooms", v)}
          canEdit
          label="bathrooms"
        />
      </InfoField>
      <InfoField label="Beds" icon="bed">
        <div className="space-y-2">
          {bedEntries.map(([type, count]) => (
            <div key={type} className="flex items-center gap-2">
              <NumberEditor
                value={count}
                onChange={(v) => onBedUpdate(type, v)}
                canEdit
                label={BED_TYPE_LABELS[type]}
                display={(n) => `${n} ${BED_TYPE_LABELS[type]}`}
              />
              <div className="flex-1" />
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => onBedUpdate(type, null)}
                aria-label={`Remove ${BED_TYPE_LABELS[type]}`}
              >
                <Icon i="close" className="size-4 text-muted-foreground" />
              </Button>
            </div>
          ))}
          {unusedBedTypes.length > 0 && (
            <BedTypeEditor unusedTypes={unusedBedTypes} onAdd={(type, count) => onBedUpdate(type, count)} />
          )}
        </div>
      </InfoField>
    </>
  );
}

interface RemoteWorkSectionProps {
  metadata: AccommodationMetadata | null;
  canEdit?: boolean;
  onUpdate?: (data: UpdatePlaceData) => Promise<void>;
}

function RemoteWorkSection({ metadata, canEdit, onUpdate }: RemoteWorkSectionProps) {
  const handleWorkspaceUpdate = (value: number | null) => {
    onUpdate?.({ metadata: applyField(metadata, "workspaces", value) });
  };

  const handleWifiUpdate = (field: "speed" | "stability", value: string) => {
    onUpdate?.({ metadata: applyWifi(metadata, field, value) });
  };

  return (
    <>
      <PanelSection.Separator />
      <PanelSection.Root>
        <PanelSection.Title>Remote work</PanelSection.Title>
        <PanelSection.Content className="space-y-3">
          {canEdit ? (
            <RemoteWorkEditor
              metadata={metadata}
              onWorkspaceUpdate={handleWorkspaceUpdate}
              onWifiUpdate={handleWifiUpdate}
            />
          ) : (
            metadata && hasWorkValues(metadata) && <p className="text-sm">{formatWorkSummary(metadata)}</p>
          )}
        </PanelSection.Content>
      </PanelSection.Root>
    </>
  );
}

interface RemoteWorkEditorProps {
  metadata: AccommodationMetadata | null;
  onWorkspaceUpdate: (value: number | null) => void;
  onWifiUpdate: (field: "speed" | "stability", value: string) => void;
}

function RemoteWorkEditor({ metadata, onWorkspaceUpdate, onWifiUpdate }: RemoteWorkEditorProps) {
  return (
    <>
      <InfoField label="Desks" icon="desk">
        <NumberEditor
          value={metadata?.workspaces}
          onChange={onWorkspaceUpdate}
          canEdit
          label="desks"
          display={(n) => pluralize(n, "desk")}
        />
      </InfoField>
      {WIFI_FIELDS.map((wifiField) => (
        <WifiFieldEditor
          key={wifiField.field}
          currentValue={metadata?.wifi?.[wifiField.field]}
          onUpdate={(v) => onWifiUpdate(wifiField.field, v)}
          {...wifiField}
        />
      ))}
    </>
  );
}

interface WifiFieldEditorProps {
  field: string;
  label: string;
  fieldIcon: IconType;
  options: { value: string; label: string; hint?: string; icon?: IconType }[];
  currentValue?: string;
  onUpdate: (value: string) => void;
}

function WifiFieldEditor({ label, fieldIcon, options, currentValue, onUpdate }: WifiFieldEditorProps) {
  return (
    <InfoField label={label} icon={fieldIcon}>
      <Select value={currentValue ?? ""} onValueChange={(v) => onUpdate(v === "_none" ? "" : v)}>
        <SelectTrigger size="sm" className="h-8 w-auto min-w-40 text-sm" aria-label={label}>
          <SelectValue placeholder={`Set ${label.toLowerCase()}...`} />
        </SelectTrigger>
        <SelectContent>
          {currentValue && (
            <SelectItem value="_none" className="text-muted-foreground">
              Not set
            </SelectItem>
          )}
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.icon && <Icon i={opt.icon} className="size-4" />}
              {opt.label}
              {opt.hint && <span className="ml-1 text-muted-foreground">{opt.hint}</span>}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </InfoField>
  );
}

interface AmenitiesSectionProps {
  selectedAmenities: Amenity[];
  availableAmenities: Amenity[];
  canEdit?: boolean;
  onAdd: (amenity: Amenity) => void;
  onRemove: (amenity: Amenity) => void;
}

function AmenitiesSection({ selectedAmenities, availableAmenities, canEdit, onAdd, onRemove }: AmenitiesSectionProps) {
  return (
    <>
      <PanelSection.Separator />
      <PanelSection.Root>
        <PanelSection.Title>Amenities</PanelSection.Title>
        <PanelSection.Content>
          {canEdit && availableAmenities.length > 0 && (
            <Select value="" onValueChange={onAdd}>
              <SelectTrigger size="sm" className="h-8 w-auto min-w-40 text-sm" aria-label="Amenities">
                <SelectValue placeholder="Add amenity..." />
              </SelectTrigger>
              <SelectContent>
                {availableAmenities.map((amenity) => (
                  <SelectItem key={amenity} value={amenity}>
                    <Icon i={amenity} className="size-4" />
                    {AMENITY_LABELS[amenity]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {selectedAmenities.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {selectedAmenities.map((amenity) => (
                <Tag key={amenity} className={cn({ "gap-1.5 py-1 pl-2.5 pr-1.5 text-sm": canEdit })}>
                  <Icon i={amenity} className="size-4" />
                  {AMENITY_LABELS[amenity]}
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => onRemove(amenity)}
                      aria-label={`Remove ${AMENITY_LABELS[amenity]}`}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Icon i="close" className="size-4" />
                    </button>
                  )}
                </Tag>
              ))}
            </div>
          )}
        </PanelSection.Content>
      </PanelSection.Root>
    </>
  );
}

const GUEST_POLICIES = [
  { field: "petsAllowed" as const, label: "Pet-friendly", icon: "pet-friendly" as IconType },
  { field: "childrenAllowed" as const, label: "Child-friendly", icon: "child-friendly" as IconType },
];

interface GuestPoliciesSectionProps {
  metadata: AccommodationMetadata | null;
  canEdit?: boolean;
  onToggle: (field: "petsAllowed" | "childrenAllowed", checked: boolean) => void;
}

function GuestPoliciesSection({ metadata, canEdit, onToggle }: GuestPoliciesSectionProps) {
  return (
    <>
      <PanelSection.Separator />
      <PanelSection.Root>
        <PanelSection.Title>Guest policies</PanelSection.Title>
        <PanelSection.Content className="space-y-3">
          {canEdit ? (
            GUEST_POLICIES.map(({ field, label, icon }) => (
              <label key={field} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-1.5">
                  <Icon i={icon} className="size-4 text-muted-foreground" />
                  {label}
                </span>
                <Switch
                  checked={!!metadata?.[field]}
                  onCheckedChange={(checked) => onToggle(field, checked)}
                  aria-label={label}
                />
              </label>
            ))
          ) : (
            <div className="flex flex-wrap gap-2">
              {GUEST_POLICIES.filter(({ field }) => metadata?.[field]).map(({ field, label, icon }) => (
                <Tag key={field}>
                  <Icon i={icon} className="size-3.5" />
                  {label}
                </Tag>
              ))}
            </div>
          )}
        </PanelSection.Content>
      </PanelSection.Root>
    </>
  );
}

function AccommodationPropertyDetails({ metadata }: { metadata: AccommodationMetadata | null }) {
  if (!metadata) return null;

  const { guests, bedrooms, bathrooms, beds } = metadata;
  const bedEntries = getBedEntries(beds);

  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
      {!!guests && (
        <div>
          <dt className="text-muted-foreground text-xs flex items-center gap-1">
            <Icon i="users" className="size-3.5" />
            Guests
          </dt>
          <dd>{pluralize(guests, "guest")}</dd>
        </div>
      )}
      {!!bedrooms && (
        <div>
          <dt className="text-muted-foreground text-xs flex items-center gap-1">
            <Icon i="bedroom" className="size-3.5" />
            Bedrooms
          </dt>
          <dd>{pluralize(bedrooms, "bedroom")}</dd>
        </div>
      )}
      {!!bathrooms && (
        <div>
          <dt className="text-muted-foreground text-xs flex items-center gap-1">
            <Icon i="bathroom" className="size-3.5" />
            Bathrooms
          </dt>
          <dd>{pluralize(bathrooms, "bathroom")}</dd>
        </div>
      )}
      {bedEntries.length > 0 && (
        <div className="col-span-2">
          <dt className="text-muted-foreground text-xs flex items-center gap-1">
            <Icon i="bed" className="size-3.5" />
            Beds
          </dt>
          <dd className="flex flex-wrap gap-x-3 gap-y-1">
            {bedEntries.map(([type, count]) => (
              <span key={type}>
                {count} {BED_TYPE_LABELS[type]}
              </span>
            ))}
          </dd>
        </div>
      )}
    </dl>
  );
}

function getBedEntries(beds: AccommodationMetadata["beds"]): [BedType, number][] {
  if (!beds) return [];
  return (Object.entries(beds) as [BedType, number | undefined][]).filter(
    (entry): entry is [BedType, number] => !!entry[1] && entry[1] > 0,
  );
}

function applyField(
  metadata: AccommodationMetadata | null,
  field: "guests" | "bedrooms" | "bathrooms" | "workspaces",
  value: number | null,
): AccommodationMetadata | null {
  const next = omitKey({ ...metadata }, field);
  if (value) next[field] = value;
  return hasValues(next) ? next : null;
}

function applyBed(
  metadata: AccommodationMetadata | null,
  bedType: BedType,
  count: number | null,
): AccommodationMetadata | null {
  const beds = omitKey({ ...metadata?.beds }, bedType);
  if (count) beds[bedType] = count;

  const next: AccommodationMetadata = omitKey({ ...metadata }, "beds");
  if (Object.keys(beds).length > 0) next.beds = beds;

  return hasValues(next) ? next : null;
}

function omitKey<T extends Record<string, unknown>>(obj: T, key: keyof T): T {
  delete obj[key];
  return obj;
}

function applyWifi(
  metadata: AccommodationMetadata | null,
  field: "speed" | "stability",
  value: string,
): AccommodationMetadata | null {
  const wifi = omitKey({ ...metadata?.wifi }, field);
  if (value) wifi[field] = value as never;

  const next: AccommodationMetadata = omitKey({ ...metadata }, "wifi");
  if (Object.keys(wifi).length > 0) next.wifi = wifi;

  return hasValues(next) ? next : null;
}

function applyPolicy(
  metadata: AccommodationMetadata | null,
  field: "petsAllowed" | "childrenAllowed",
  checked: boolean,
): AccommodationMetadata | null {
  const next = omitKey({ ...metadata }, field);
  if (checked) next[field] = true;
  return hasValues(next) ? next : null;
}

function applyAmenityRemoval(metadata: AccommodationMetadata | null, amenity: Amenity): AccommodationMetadata | null {
  const amenities = (metadata?.amenities ?? []).filter((a) => a !== amenity);
  const next = { ...metadata };
  if (amenities.length > 0) {
    next.amenities = amenities;
  } else {
    delete next.amenities;
  }
  return hasValues(next) ? next : null;
}

function sectionVisibility(
  metadata: AccommodationMetadata | null,
  canEdit: boolean | undefined,
  selectedAmenities: Amenity[],
) {
  return {
    property: canEdit || hasPropertyValues(metadata),
    guestPolicies: canEdit || hasGuestPolicyValues(metadata),
    remoteWork: canEdit || hasWorkValues(metadata),
    amenities: canEdit || selectedAmenities.length > 0,
  };
}

function hasPropertyValues(meta: AccommodationMetadata | null): boolean {
  if (!meta) return false;
  return !!(meta.guests || meta.bedrooms || meta.bathrooms || (meta.beds && Object.keys(meta.beds).length > 0));
}

function hasGuestPolicyValues(meta: AccommodationMetadata | null): boolean {
  if (!meta) return false;
  return !!(meta.petsAllowed || meta.childrenAllowed);
}

function hasWorkValues(meta: AccommodationMetadata | null): boolean {
  if (!meta) return false;
  return !!(meta.workspaces || meta.wifi?.speed || meta.wifi?.stability);
}

function hasValues(meta: AccommodationMetadata): boolean {
  return Object.values(meta).some(isPresent);
}

function isPresent(v: unknown): boolean {
  if (v == null) return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === "object") return Object.keys(v).length > 0;
  return true;
}
